import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from '../i18n';
import AuthMenu from '../components/auth/AuthMenu';
import { Instagram, Menu, X, MapPin, Clock, ChevronRight } from 'lucide-react';
import logoImage80 from '../assets/logo.jpg?w=80&format=jpg&imagetools';
import logoImage160 from '../assets/logo.jpg?w=160&format=jpg&imagetools';
import logoImage240 from '../assets/logo.jpg?w=240&format=jpg&imagetools';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../lib/ogImage';
import { useConsent } from '../context/ConsentContext';

const URL = 'https://210tattoo.gr/athens-tattoo-studio-locations';

const NEIGHBOURHOODS = [
  { name: 'Εξάρχεια', href: '/blog/tattoo-studio-near-exarchia', distance: '10 με 12 λεπτά με τα πόδια' },
  { name: 'Νεάπολη', href: '/blog/tattoo-studio-near-neapoli-exarchia', distance: '5 με 8 λεπτά με τα πόδια' },
  { name: 'Κυψέλη', href: '/blog/tattoo-studio-near-kypseli', distance: '12 με 15 λεπτά με τα πόδια ή 2 με 3 στάσεις λεωφορείου' },
  { name: 'Αμπελόκηποι', href: '/blog/tattoo-studio-near-ambelokipi', distance: 'Λίγα λεπτά από το Πανόρμου' },
  { name: 'Κολωνάκι', href: '/blog/tattoo-studio-near-kolonaki', distance: 'Λίγα λεπτά από την πλατεία Δεξαμενής μέσω Λυκαβηττού' },
  { name: 'Παγκράτι', href: '/blog/tattoo-studio-near-pagkrati', distance: 'Περίπου 15 λεπτά με λεωφορείο ή ταξί' },
  { name: 'Σύνταγμα & Πλάκα', href: '/blog/tattoo-studio-near-syntagma-monastiraki', distance: 'Λίγα λεπτά από το ιστορικό κέντρο' },
  { name: 'Πετράλωνα', href: '/blog/tattoo-studio-near-petralona', distance: '20 με 25 λεπτά με το μετρό (γραμμή 3) ή 15 με 20 λεπτά με ταξί' },
];

export default function AthensTattooStudioLocations() {
  const { t, i18n } = useTranslation();
  const { openManager } = useConsent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="text-white min-h-screen relative flex flex-col bg-black">
      <Helmet>
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <title>Περιοχές που Εξυπηρετούμε στην Αθήνα | 210 Tattoo Studio</title>
        <meta name="description" content="Το 210 Tattoo Athens εξυπηρετεί όλη την Αθήνα. Δες πόσο κοντά είσαι από τη γειτονιά σου, δεχόμαστε ραντεβού και walk-in από παντού." />
        <link rel="canonical" href={URL} />
        <meta property="og:title" content="Περιοχές που Εξυπηρετούμε στην Αθήνα | 210 Tattoo Studio" />
        <meta property="og:description" content="Το 210 Tattoo Athens εξυπηρετεί όλη την Αθήνα. Δες πόσο κοντά είσαι από τη γειτονιά σου, δεχόμαστε ραντεβού και walk-in από παντού." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage('athens-tattoo-studio-locations')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Περιοχές που Εξυπηρετούμε στην Αθήνα | 210 Tattoo Studio" />
        <meta name="twitter:description" content="Δες πόσο κοντά είσαι από τη γειτονιά σου. Δεχόμαστε ραντεβού και walk-in από όλη την Αθήνα." />
        <meta name="twitter:image" content={ogImage('athens-tattoo-studio-locations')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Περιοχές που Εξυπηρετούμε στην Αθήνα",
          "description": "Το 210 Tattoo Athens εξυπηρετεί πελάτες από όλη την Αθήνα, με ραντεβού και walk-in τατουάζ ανεξάρτητα από τη γειτονιά.",
          "provider": { "@id": "https://210tattoo.gr/#tattooparlor" },
          "areaServed": [
            { "@type": "City", "name": "Athens" },
            { "@type": "Place", "name": "Εξάρχεια" },
            { "@type": "Place", "name": "Νεάπολη" },
            { "@type": "Place", "name": "Κυψέλη" },
            { "@type": "Place", "name": "Αμπελόκηποι" },
            { "@type": "Place", "name": "Κολωνάκι" },
            { "@type": "Place", "name": "Παγκράτι" },
            { "@type": "Place", "name": "Σύνταγμα & Πλάκα" },
            { "@type": "Place", "name": "Πετράλωνα" }
          ],
          "url": URL
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://210tattoo.gr/" },
            { "@type": "ListItem", "position": 2, "name": "Περιοχές που Εξυπηρετούμε", "item": URL }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Δέχεστε πελάτες από όλη την Αθήνα;", "acceptedAnswer": { "@type": "Answer", "text": "Ναι. Αν και αρκετοί πελάτες μας έρχονται από τις κοντινές γειτονιές γύρω από τη Γκύζη, δεχόμαστε ραντεβού και walk-in από ολόκληρη την πόλη." } },
            { "@type": "Question", "name": "Χρειάζεται ραντεβού αν έρχομαι από μακριά;", "acceptedAnswer": { "@type": "Answer", "text": "Όχι απαραίτητα. Δεχόμαστε walk-in τατουάζ ανεξάρτητα από την απόσταση, αν και ένα μήνυμα στο Instagram πριν έρθεις βοηθάει να ξέρεις σίγουρα ότι υπάρχει διαθέσιμος artist τη στιγμή που θα φτάσεις." } },
            { "@type": "Question", "name": "Ποια είναι η πιο κοντινή γειτονιά στο studio;", "acceptedAnswer": { "@type": "Answer", "text": "Η Νεάπολη και τα Εξάρχεια είναι οι πιο κοντινές, μόλις 5 με 12 λεπτά με τα πόδια." } }
          ]
        })}</script>
      </Helmet>

      {/* Navigation — matches the other service pages */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b border-pink-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex-shrink-0 flex items-center">
              <a href="/" className="flex items-center gap-3">
                <picture>
                  <source srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} type="image/jpeg" />
                  <img src={logoImage80} srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} alt="210 Tattoo Studio" className="h-20 w-auto rounded-lg object-contain" width={80} height={80} loading="eager" decoding="async" />
                </picture>
              </a>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                <a href="/#home" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.home')}</a>
                <div className="relative group">
                  <button className="px-3 py-2 text-sm font-medium text-gray-300 group-hover:text-pink-400">{t('nav.studioMenu')}</button>
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-48 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                    <a href="/#about" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.about')}</a>
                    <a href="/#services" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.services')}</a>
                  </div>
                </div>
                <a href="/#gallery" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.gallery')}</a>
                <a href="/blog" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.blog')}</a>
                <div className="relative group">
                  <button className="px-3 py-2 text-sm font-medium text-gray-300 group-hover:text-pink-400">{t('nav.helpMenu')}</button>
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-56 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                    <a href="/#faq" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.faq')}</a>
                    <a href="/#contact" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.contact')}</a>
                  </div>
                </div>
                <a href="/spin" className="px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-600 to-pink-700 rounded-lg hover:from-pink-700 hover:to-pink-800 shadow-lg hover:shadow-pink-500/25">{t('nav.spin')}</a>
                <div className="flex items-center gap-2 pl-4 border-l border-pink-600/20">
                  <button onClick={() => loadLanguage('el')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'el' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to Greek">ΕΛ</button>
                  <button onClick={() => loadLanguage('en')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'en' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to English">EN</button>
                </div>
                <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-pink-500/25">
                  <Instagram size={16} />
                  {t('nav.dmCta')}
                </a>
                <AuthMenu />
              </div>
            </div>
            <div className="md:hidden flex items-center gap-2">
              <AuthMenu />
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-300 hover:text-white p-2" aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={isMenuOpen} aria-controls="mobile-menu">
                {isMenuOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
              </button>
            </div>
          </div>
        </div>
        {isMenuOpen && (
          <div id="mobile-menu" className="md:hidden bg-black border-t border-pink-600/20">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {(['home','about','services','gallery','faq','contact'] as const).map((id) => (
                <a key={id} href={`/#${id}`} className="block px-3 py-2 text-base font-medium w-full text-left text-gray-300 hover:text-pink-400">{t(`nav.${id}` as any)}</a>
              ))}
              <a href="/blog" className="block px-3 py-2 text-base font-medium w-full text-left text-gray-300 hover:text-pink-400">{t('nav.blog')}</a>
              <a href="/spin" className="block px-3 py-2 text-base font-medium w-full text-left text-pink-300 hover:text-pink-400">{t('nav.spin')}</a>
              <div className="flex items-center gap-2 mt-2 px-3">
                <button onClick={() => loadLanguage('el')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'el' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>ΕΛ</button>
                <button onClick={() => loadLanguage('en')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'en' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>EN</button>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 px-3 pb-3 items-center justify-items-center mx-3">
                <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-3 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 justify-self-center">
                  <Instagram size={16} />
                  {t('nav.dmCta')}
                </a>
                <div className="flex justify-center justify-self-center"><AuthMenu /></div>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-28 flex-1">

        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Περιοχές που Εξυπηρετούμε</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">
                Περιοχές που Εξυπηρετούμε
              </span>
              <br />
              <span className="text-white">στην Αθήνα</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 leading-relaxed max-w-2xl">
              Το 210 Tattoo Athens δεν εξυπηρετεί μόνο τη γειτονιά μας. Ερχόμαστε σε επαφή με πελάτες από όλη την Αθήνα, και δεχόμαστε τόσο ραντεβού όσο και <Link to="/walk-in-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">walk-in τατουάζ</Link> ανεξάρτητα από το πού μένεις.
            </p>
            <p className="text-lg text-gray-400 mb-10 leading-relaxed max-w-2xl">
              210 Tattoo Athens doesn't just serve our own neighbourhood. We work with clients from across the city, and we welcome both booked appointments and walk-in tattoos no matter where you're coming from.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://www.instagram.com/210tattoo/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105"
              >
                <Instagram size={20} />
                Κλείστε Ραντεβού / Book Now
              </a>
              <a
                href="https://maps.google.com/?q=Αναστασίου+Γενναδίου+34,+Αθήνα"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-pink-500/40 text-gray-300 hover:border-pink-500 hover:text-white px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 flex items-center justify-center gap-3"
              >
                <MapPin size={20} />
                Βρείτε μας / Find Us
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Δείτε επίσης: <Link to="/walk-in-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">Walk-In Tattoo Αθήνα</Link>
            </p>
          </div>
        </section>

        {/* Πόσο Κοντά Είσαι */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-4xl font-bold text-white mb-4">
                Πόσο <span className="text-pink-500">Κοντά Είσαι;</span>
              </h2>
              <p className="text-gray-400 text-lg">How close are you?</p>
            </div>
            <p className="text-gray-300 leading-relaxed mb-8 max-w-2xl">
              Αν μένεις σε μία από τις παρακάτω περιοχές, το studio είναι πιο κοντά σου απ' όσο ίσως νομίζεις:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {NEIGHBOURHOODS.map((n) => (
                <Link key={n.href} to={n.href} className="bg-black rounded-xl p-5 border border-pink-600/10 hover:border-pink-500/40 transition-colors duration-200 group">
                  <p className="text-white font-medium group-hover:text-pink-400 transition-colors">{n.name}</p>
                  <p className="text-gray-500 text-sm mt-1">{n.distance}</p>
                </Link>
              ))}
            </div>
            <p className="text-gray-400 leading-relaxed mt-8 max-w-2xl">
              If you live in one of the areas above, the studio is closer than you might think, distances and transit notes for each are in the linked guides.
            </p>
          </div>
        </section>

        {/* Και αν Μένεις Κάπου Αλλού */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  Και αν Μένεις <span className="text-pink-500">Κάπου Αλλού;</span>
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Δεν χρειάζεται να μένεις σε μία από τις παραπάνω γειτονιές για να έρθεις σε εμάς. Το 210 Tattoo Athens δέχεται πελάτες από ολόκληρη την Αθήνα, και αν θες να κάνεις τατουάζ αυθημερόν χωρίς προγραμματισμένο ραντεβού, δεχόμαστε <Link to="/walk-in-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">walk-in</Link> ανεξάρτητα από την περιοχή που έρχεσαι. Απλά πέρνα από το studio, ή στείλε μας πρώτα ένα μήνυμα στο Instagram για να επιβεβαιώσεις ότι υπάρχει διαθέσιμος artist.
                </p>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  What if you're <span className="text-pink-500">somewhere else?</span>
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  You don't need to live in one of the neighbourhoods above to come see us. 210 Tattoo Athens welcomes clients from across the whole city, and if you want a same-day tattoo without a pre-booked appointment, we accept <Link to="/walk-in-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">walk-ins</Link> no matter which part of Athens you're coming from. Just stop by the studio, or send us a message on Instagram first to confirm an artist is available.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Location & CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  Πού θα μας <span className="text-pink-500">βρείτε</span>
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-pink-600 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                      <MapPin className="text-white" size={18} />
                    </div>
                    <div>
                      <p className="text-white font-medium">210 Tattoo Athens</p>
                      <p className="text-gray-400">Αναστασίου Γενναδίου 34</p>
                      <p className="text-gray-400">Γκύζη, Αθήνα 114 72</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-pink-600 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                      <Clock className="text-white" size={18} />
                    </div>
                    <div>
                      <p className="text-white font-medium">Ώρες Λειτουργίας</p>
                      <p className="text-gray-400">Δευτέρα–Παρασκευή: 11:00–20:00</p>
                      <p className="text-gray-400">Σάββατο: 11:00–19:00</p>
                      <p className="text-gray-500">Κυριακή: Κλειστά</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Έτοιμος να <span className="text-pink-500">έρθεις;</span>
                </h2>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  Ανεξάρτητα από το πού μένεις στην Αθήνα, στείλε μας μήνυμα στο Instagram για να κλείσουμε ραντεβού, ή πέρνα walk-in όποτε σου βολεύει.
                </p>
                <a
                  href="https://www.instagram.com/210tattoo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-3 w-fit shadow-lg hover:shadow-pink-500/25 transform hover:scale-105"
                >
                  <Instagram size={20} />
                  DM us on Instagram
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-10">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                <h3 className="text-white font-semibold mb-2">Δέχεστε πελάτες από όλη την Αθήνα;</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Ναι. Αν και αρκετοί πελάτες μας έρχονται από τις κοντινές γειτονιές γύρω από τη Γκύζη, δεχόμαστε ραντεβού και walk-in από ολόκληρη την πόλη.</p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                <h3 className="text-white font-semibold mb-2">Χρειάζεται ραντεβού αν έρχομαι από μακριά;</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Όχι απαραίτητα. Δεχόμαστε <Link to="/walk-in-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">walk-in τατουάζ</Link> ανεξάρτητα από την απόσταση, αν και ένα μήνυμα στο Instagram πριν έρθεις βοηθάει να ξέρεις σίγουρα ότι υπάρχει διαθέσιμος artist τη στιγμή που θα φτάσεις.</p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                <h3 className="text-white font-semibold mb-2">Ποια είναι η πιο κοντινή γειτονιά στο studio;</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Η Νεάπολη και τα Εξάρχεια είναι οι πιο κοντινές, μόλις 5 με 12 λεπτά με τα πόδια. Δες την πλήρη λίστα παραπάνω για την περιοχή σου.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Other services */}
        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">
              Άλλες Υπηρεσίες <span className="text-pink-500">/ Other Services</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/walk-in-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Walk-In Tattoo Athens</Link>
              <Link to="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</Link>
              <Link to="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</Link>
              <Link to="/sleeve-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Sleeve Tattoo Athens</Link>
              <Link to="/blackwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Blackwork Tattoo Athens</Link>
            </div>
          </div>
        </section>

      </main>

      {/* Footer — matches the other service pages */}
      <footer className="bg-black py-12 border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-pink-500 mb-4">{t('footer.studio')}</h3>
            <p className="text-gray-400 mb-6">{t('footer.tagline')}</p>
            <div className="flex justify-center space-x-6 mb-8">
              <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition-colors duration-200" aria-label={t('footer.instagramLabel', 'Visit our Instagram profile')}>
                <Instagram size={24} aria-hidden />
              </a>
            </div>
            <nav className="mb-8">
              <ul className="flex justify-center gap-6 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">{t('footer.privacy', 'Privacy Policy')}</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">{t('footer.cookies', 'Cookies Policy')}</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">{t('footer.terms', 'Terms of Service')}</Link></li>
                <li>
                  <button type="button" onClick={openManager} className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    {t('consent.manage')}
                  </button>
                </li>
              </ul>
            </nav>
            <div className="border-t border-gray-800 pt-8">
              <p className="text-gray-300 text-sm">{t('footer.rights')}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
